import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getApiUserUserIdPriceCalculator } from '../../api/interconectar-api';
import { GetApiUserUserIdPriceCalculatorParams } from '../../api/models/getApiUserUserIdPriceCalculatorParams';
import { PriceCalculatorBriefViewModelCollectionResult } from '../../api/models/priceCalculatorBriefViewModelCollectionResult';
import { Sorted } from '../../api/models/sorted';
import avatarMen from '../../assets/images/avatar-men.png';
import avatarWomen from '../../assets/images/avatar-women.png';
import { useAuth } from '../../context/AuthContext';
import { useRemainingCalculations } from '../../hooks/useRemainingCalculations';
import CircularLoading from '../../shared/CircularLoading/CircularLoading';
import './PriceCalculator.css';
import PriceCalculatorResumen from './PriceCalculatorResume';
import PriceCalculatorStepFour from './PriceCalculatorStepFour';
import PriceCalculatorStepOne from './PriceCalculatorStepOne';
import PriceCalculatorStepThree from './PriceCalculatorStepThree';
import PriceCalculatorStepTwo from './PriceCalculatorStepTwo';
import { useAvatar } from '../../hooks/useAvatar';
import { IconSecure } from '../../components/Icons/IconSecure';
interface Props {
	onShowtitle: (itemshow: boolean) => void;
	handleClose: () => void;
	handleClickOpen: () => void;
	showVideoComponent: boolean;
	selectedItemId?: string | null;
	setSelectedItemId: (itemId: string | null) => void;
	setIsNewCalculator: (isNewCalculator: boolean) => void;
	refetchRemainingCalcs: () => Promise<void>;
	onBackToModalCalculadora?: () => void;
}

const PriceCalculator: React.FC<Props> = ({
	onShowtitle,
	handleClose,
	handleClickOpen,
	showVideoComponent,
	selectedItemId,
	setSelectedItemId,
	setIsNewCalculator,
	refetchRemainingCalcs,
	onBackToModalCalculadora,
}) => {
	const { t } = useTranslation();
	const { state } = useAuth();
	const [activeTab, setActiveTab] = useState(0);
	const {
		data: remainingCalcs,
		loading: loadingCalcs,
		error: errorCalcs,
	} = useRemainingCalculations(process.env.REACT_APP_PRICE_CALCULATOR_ID || '353');
	const [summaryTab, setSummaryTab] = useState(false);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [data, setData] = useState<any[]>([]);
	const [selectedFilter, setSelectedFilter] = useState<Sorted>(Sorted.DESC);
	const [summaryTabState, setSummaryTabState] = useState(false);
	const avatar = useAvatar();

	useEffect(() => {
		const selectedItemData = localStorage.getItem('selectedItemData');
		if (selectedItemData) {
			localStorage.removeItem('selectedItemData');
		}
	}, []);

	const tabs = [
		{
			title: t('price_calculator.tab.title_1'),
			id: 0,
		},
		{
			title: t('price_calculator.tab.title_2'),
			id: 1,
		},
		{
			title: t('price_calculator.tab.title_3'),
			id: 2,
		},
		{
			title: t('price_calculator.tab.title_4'),
			id: 3,
		},
	];

	const handleTabClick = (index: number) => {
		if (activeTab > index) {
			setActiveTab(index);
		}
	};

	const renderSwitch = (activeTab: Number) => {
		switch (activeTab) {
			case 0:
				return (
					<PriceCalculatorStepOne
						handleClickOpen={handleClickOpen}
						handleContinueClick={handleContinueClick}
						selectedItemId={selectedItemId}
					/>
				);
			case 1:
				return (
					<PriceCalculatorStepTwo
						handleClickOpen={handleClickOpen}
						handleContinueClick={handleContinueClick}
					/>
				);
			case 2:
				return (
					<PriceCalculatorStepThree
						handleClickOpen={handleClickOpen}
						handleContinueClick={handleContinueClick}
					/>
				);
			case 3:
				return (
					<PriceCalculatorStepFour
						handleClickOpen={handleClickOpen}
						handleContinueClick={handleContinueClick}
					/>
				);
		}
	};

	const handleContinueClick = () => {
		setSummaryTab(false);
		onShowtitle(true);
		if (activeTab < tabs.length - 1) {
			setActiveTab(activeTab + 1);
		} else {
			onShowtitle(false);
			setSummaryTab(true);
		}
	};

	useEffect(() => {
		if (!showVideoComponent) {
			resetForm();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	function resetForm() {
		onShowtitle(true);
		setSummaryTab(false);
		setActiveTab(0);
		window.localStorage.removeItem('formOne');
		window.localStorage.removeItem('formTwo');
		window.localStorage.removeItem('formThree');
		window.localStorage.removeItem('formFour');
		localStorage.removeItem('formOneChanged');
		localStorage.removeItem('formTwoChanged');
		localStorage.removeItem('formThreeChanged');
		localStorage.removeItem('formFourChanged');
		localStorage.removeItem('importerCityChanged');
		localStorage.removeItem('retailerCityChanged');
		localStorage.removeItem('retailTruckTypeChanged');
	}

	const handleButtonClick = () => {
		// If there's a selected item, load its data
		if (selectedItemId) {
			// The data will be loaded in PriceCalculatorStepOne when it receives the selectedItemId
			setActiveTab(0);
			setSummaryTab(false);
		} else {
			// If no item is selected, just reset the form
			resetForm();
		}
	};

	const fetchData = async () => {
		setLoading(true);
		setError(null);
		setData([]);

		if (!state?.isAuthenticated) {
			setLoading(false);
			return;
		}

		try {
			const userId = localStorage.getItem('userId');
			if (!userId) {
				setError(t('price_calculator.step_1.error_message_user_1'));
				return;
			}

			const params: GetApiUserUserIdPriceCalculatorParams = {
				size: 100,
				order: selectedFilter,
			};

			const response: PriceCalculatorBriefViewModelCollectionResult = await getApiUserUserIdPriceCalculator(
				userId,
				params,
			);

			if (response?.results) {
				setData([...response.results]);
			} else {
				setError(t('price_calculator.step_1.error_message_user_2'));
			}
		} catch (err: any) {
			setError(err.message || t('price_calculator.step_1.error_message_user_3'));
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (!state?.isAuthenticated) {
			setLoading(false);
			return;
		}
		fetchData();
	}, []);

	// const handleItemClick = (itemId: string) => {
	//   setSelectedItemId(itemId);
	//   setActiveTab(0); // Go to first step
	//   setSummaryTab(false);
	// };

	useEffect(() => {
		if (state?.isAuthenticated) {
			const formOne = localStorage.getItem('formOne');
			const formTwo = localStorage.getItem('formTwo');
			const formThree = localStorage.getItem('formThree');
			const formFour = localStorage.getItem('formFour');

			// Solo establecer summaryTab en true si no hay selectedItemId y hay datos en los formularios
			if (!selectedItemId && (formOne || formTwo || formThree || formFour)) {
				setSummaryTabState(true);
				setSummaryTab(true);
			} else if (selectedItemId) {
				// Si hay un selectedItemId, asegurarse de que no estamos en la pestaña de resumen
				setSummaryTabState(false);
				setSummaryTab(false);
			}
			// refetchRemainingCalcs();
		}
		// console.log("🚀 ~ useEffect ~ refetchRemainingCalcs:", refetchRemainingCalcs)
	}, [state?.isAuthenticated, setSummaryTab, selectedItemId]);

	useEffect(() => {
		localStorage.setItem('summaryTab', summaryTabState.toString());
		setSummaryTab(summaryTabState); // Sincronizar el estado con el padre
	}, [summaryTabState, setSummaryTab]);

	// Debug when refetchRemainingCalcs is called
	const handleRefetch = async () => {
		if (!state?.isAuthenticated) {
			setLoading(false);
			return;
		}
		try {
			await refetchRemainingCalcs();
			// console.log('refetchRemainingCalcs completed successfully');
		} catch (error) {
			// console.error('Error in refetchRemainingCalcs:', error);
		}
	};

	if (loading) {
		return <CircularLoading />;
	}

	if (error) {
		return <div>{error}</div>;
	}
	return (
		<>
			<div className="price-calculator__container">
				<div className="price-calculator__price-calculator-container-main">
					{summaryTab ? (
						<PriceCalculatorResumen
							resetFormClick={resetForm}
							setSelectedItemId={setSelectedItemId}
							setIsNewCalculator={setIsNewCalculator}
							setSummaryTab={setSummaryTab}
							refetchRemainingCalcs={handleRefetch}
							onShowtitle={onShowtitle}
							onBackToModalCalculadora={onBackToModalCalculadora}
						/>
					) : (
						<>
							<div className="price-calculator__tabs">
								{tabs.map((tab, index) => (
									<div
										key={index}
										className={`tab-price-calculator ${activeTab === index ? 'tab-active' : ''}`}
										onClick={() => handleTabClick(index)}>
										<div
											className={`price-calculator__circle ${
												activeTab === index ? 'price-calculator__circle--active' : ''
											}`}>
											{index + 1}
										</div>
										<div
											className={`text-price-calculator ${activeTab === index ? 'text-price-calculator-active' : ''}`}>
											{tab.title}
										</div>
									</div>
								))}
							</div>
							<div className="price-calc__tab-container price-calculator__tab-container">
								<div className="helper-container">
									<div className="helper-container__left">
										<img
											src={avatar === 'women' ? avatarWomen : avatarMen}
											alt={avatar === 'women' ? 'avatar women' : 'avatar men'}
											className="helper-container__left__image"
											width={24}
											height={24}
										/>
									</div>
									<div className="helper-container__right">
										{!state?.isAuthenticated || !remainingCalcs ? (
											<div
												className="helper-container__left__title"
												dangerouslySetInnerHTML={{ __html: t('price_calculator.helper.description') }}
											/>
										) : (
											<div
												className="helper-container__left__title"
												dangerouslySetInnerHTML={{ __html: t('price_calculator.helper.description_pro') }}
											/>
										)}
									</div>
								</div>
								<div className="price-calculator-container_father">{renderSwitch(activeTab)}</div>
							</div>
						</>
					)}
				</div>
			</div>
			<span className="price-calculator-footer">
				<IconSecure />
				{t('exporterTest.secure')}
			</span>
		</>
	);
};

export default PriceCalculator;
